import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Mail, Send, UserPlus, Building, Crown, Shield, User, PencilLine } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { useInvitationPermissions } from "@/hooks/use-invitation-permissions";
import { DeviceMultiSelect } from "@/components/common/DeviceMultiSelect";
import type { Contact } from "@/hooks/use-contacts";
import { CopyInvitationLinkButton } from "./CopyInvitationLinkButton";

interface ContactUserBridgeProps {
  contact: Contact;
  hasPendingInvitation?: boolean;
}

export const ContactUserBridge = ({ contact, hasPendingInvitation = false }: ContactUserBridgeProps) => {
  const [inviteOpen, setInviteOpen] = useState(false);
  // Use intended_system_role as initial role if available, otherwise default to 'standard'
  const [selectedRole, setSelectedRole] = useState<'standard' | 'editor' | 'admin' | 'superuser'>(
    contact.intended_system_role || 'standard'
  );
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: permissions } = useInvitationPermissions();

  const handleSendInvitation = async () => {
    if (!permissions?.canSendInvitations) {
      toast({
        title: "Permission Denied",
        description: "You don't have permission to send invitations",
        variant: "destructive",
      });
      return;
    }

    // Validate that contact has an organization
    if (!contact.organization_id) {
      toast({
        title: "Missing Organization",
        description: "Contact must be assigned to an organization before sending an invitation",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    console.log("Creating invitation for contact:", {
      contactId: contact.id,
      contactEmail: contact.email,
      organizationId: contact.organization_id,
      selectedRole
    });

    try {
      // Get current user for invited_by field
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error("User not authenticated");
      }

      console.log("Current user creating invitation:", user.id);

      // Check user's permissions before attempting invitation
      const { data: userRole, error: roleError } = await supabase
        .from('user_roles')
        .select('role, organization_id')
        .eq('user_id', user.id)
        .single();
        
      if (roleError) {
        console.error("Error fetching user role:", roleError);
        throw new Error("Could not verify user permissions");
      }
      
      console.log("User role check:", {
        userRole: userRole?.role,
        userOrg: userRole?.organization_id,
        contactOrg: contact.organization_id
      });
      
      // Validate admin can only invite to their org
      if (userRole?.role === 'admin' && userRole.organization_id !== contact.organization_id) {
        throw new Error(`You can only send invitations to contacts in your organization. Contact org: ${contact.organization_id}, Your org: ${userRole.organization_id}`);
      }
      
      if (!contact.organization_id) {
        throw new Error("Contact must be assigned to an organization before sending an invitation");
      }

      // Get current user's profile for inviter name
      const { data: inviterProfile } = await supabase
        .from('profiles')
        .select('first_name, last_name')
        .eq('user_id', user.id)
        .single();

      // Check if an invitation already exists for this email + organization
      const { data: existingInvitation } = await supabase
        .from('user_invitations')
        .select('id')
        .eq('email', contact.email)
        .eq('organization_id', contact.organization_id)
        .maybeSingle();

      let invitation;
      
      if (existingInvitation) {
        // UPDATE existing invitation (refresh expiry and role)
        console.log("Updating existing invitation:", existingInvitation.id);
        const { data: updatedInvitation, error: updateError } = await supabase
          .from('user_invitations')
          .update({
            invited_role: selectedRole,
            invited_by: user.id,
            device_ids: selectedDeviceIds,
            expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
            status: 'pending' // Reset status to pending if it was expired
          })
          .eq('id', existingInvitation.id)
          .select()
          .single();

        if (updateError) {
          console.error("Error updating invitation:", updateError);
          throw new Error(updateError.message || "Failed to update invitation");
        }
        
        invitation = updatedInvitation;
      } else {
        // INSERT new invitation
        console.log("Creating new invitation");
        const { data: newInvitation, error: insertError } = await supabase
          .from('user_invitations')
          .insert({
            email: contact.email,
            organization_id: contact.organization_id,
            invited_role: selectedRole,
            invited_by: user.id,
            device_ids: selectedDeviceIds,
            expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
          })
          .select()
          .single();

        if (insertError) {
          console.error("Error creating invitation:", insertError);
          throw new Error(insertError.message || "Failed to create invitation");
        }
        
        invitation = newInvitation;
      }

      console.log("Created invitation successfully:", invitation);

      // Get organization name for the email
      const { data: orgData } = await supabase
        .from('organizations')
        .select('name')
        .eq('id', contact.organization_id)
        .single();

      const organizationName = orgData?.name || 'Your Organization';

      // Generate correct invite link using URL template
      const { data: urlTemplateData } = await supabase
        .from('url_templates')
        .select('url_template')
        .eq('template_type', 'user_invitation')
        .eq('is_active', true)
        .single();

      let inviteLink: string;
      if (urlTemplateData?.url_template) {
        // Use custom URL template
        const templateVariables = {
          baseUrl: window.location.origin,
          invitationToken: invitation.id,
          contactId: contact.id,
          organizationId: contact.organization_id || '',
          invitedRole: selectedRole
        };
        
        inviteLink = Object.entries(templateVariables).reduce((result, [key, value]) => {
          const placeholder = `{{${key}}}`;
          return result.replace(new RegExp(placeholder, 'g'), String(value || ''));
        }, urlTemplateData.url_template);
      } else {
        // Fallback to default URL format
        inviteLink = `${window.location.origin}/invite-contact?token=${invitation.id}&contact_id=${contact.id}`;
      }
      
      console.log("Generated invite link:", inviteLink);

      // Prepare enhanced email data
      const inviterName = inviterProfile 
        ? `${inviterProfile.first_name} ${inviterProfile.last_name}`.trim()
        : undefined;

      const contactName = `${contact.first_name} ${contact.last_name}`.trim() || undefined;

      // Send the invitation email with enhanced data including template variables
      const { error: emailError } = await supabase.functions.invoke('send-invitation', {
        body: {
          email: contact.email,
          organizationName,
          inviteLink, // Fallback URL
          contactName,
          inviterName,
          contactRole: selectedRole,
          // Template variables for URL generation
          contactId: contact.id,
          invitationToken: invitation.id,
          organizationId: contact.organization_id || '',
          invitedRole: selectedRole,
          baseUrl: window.location.origin,
        }
      });

      if (emailError) {
        console.error("Error sending invitation email:", emailError);
        throw new Error(
          `Failed to send invitation email${emailError.message ? `: ${emailError.message}` : ""}`
        );
      }

      console.log("Invitation email sent successfully");

      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      queryClient.invalidateQueries({ queryKey: ["contact-invitation-status", contact.email] });
      
      toast({
        title: hasPendingInvitation ? "Invitation Resent" : "Invitation Sent",
        description: `Professional invitation sent to ${contact.email} with ${selectedRole} role`,
      });

      setInviteOpen(false);
      setSelectedRole('standard');
      setSelectedDeviceIds([]);
    } catch (error) {
      console.error("Full invitation error:", error);
      const errorMessage = error instanceof Error ? error.message : "Failed to send invitation";
      
      toast({
        title: "Error Sending Invitation",
        description: errorMessage.includes("organization") || errorMessage.includes("permission")
          ? errorMessage 
          : `Failed to send invitation: ${errorMessage}. Please check your permissions and try again.`,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'superuser':
        return <Crown className="h-4 w-4" />;
      case 'admin':
        return <Shield className="h-4 w-4" />;
      case 'editor':
        return <PencilLine className="h-4 w-4" />;
      default:
        return <User className="h-4 w-4" />;
    }
  };

  const getRoleDescription = (role: string) => {
    switch (role) {
      case 'superuser':
        return "Full system access across all organizations";
      case 'admin':
        return "Administrative access within the organization";
      case 'editor':
        return "Standard user access plus device configuration edit rights";
      default:
        return "Standard user access";
    }
  };

  // Don't show button if user can't send invitations
  if (!permissions?.canSendInvitations) {
    return null;
  }

  return (
    <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
      <div className="flex items-center gap-2 w-full">
        <DialogTrigger asChild>
          <Button 
            variant="outline" 
            size="sm" 
            className="flex-1 hover:shadow-sm transition-all duration-200 font-semibold text-black bg-white"
            style={{ borderColor: "#FB4F2B" }}
          >
            <Mail className="h-3 w-3 mr-1.5" />
            {hasPendingInvitation ? "Resend" : "Invite"}
          </Button>
        </DialogTrigger>
        <CopyInvitationLinkButton contact={contact} />
      </div>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{hasPendingInvitation ? "Resend User Invitation" : "Send User Invitation"}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Contact Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div>
                <p className="font-medium">{contact.first_name} {contact.last_name}</p>
                <p className="text-sm text-muted-foreground">{contact.email}</p>
              </div>
              {contact.organizations && (
                <div className="flex items-center gap-2">
                  <Building className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">{contact.organizations.name}</span>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium">
                Assign Role
                {contact.intended_system_role && (
                  <span className="ml-2 text-xs text-blue-600 font-normal">
                    (Recommended: {contact.intended_system_role})
                  </span>
                )}
              </label>
              <Select value={selectedRole} onValueChange={(value: 'standard' | 'editor' | 'admin' | 'superuser') => setSelectedRole(value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4" />
                      <span>Standard User</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="editor">
                    <div className="flex items-center gap-2">
                      <PencilLine className="h-4 w-4" />
                      <span>Standard User with Edit Rights</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="admin">
                    <div className="flex items-center gap-2">
                      <Shield className="h-4 w-4" />
                      <span>Organization Admin</span>
                    </div>
                  </SelectItem>
                  {permissions?.userRole === 'superuser' && (
                    <SelectItem value="superuser">
                      <div className="flex items-center gap-2">
                        <Crown className="h-4 w-4" />
                        <span>Superuser</span>
                      </div>
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="bg-slate-50 p-3 rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                {getRoleIcon(selectedRole)}
                <span className="text-sm font-medium capitalize">{selectedRole}</span>
                {contact.intended_system_role && selectedRole === contact.intended_system_role && (
                  <Badge variant="secondary" className="text-xs">Matches intended role</Badge>
                )}
                {contact.intended_system_role && selectedRole !== contact.intended_system_role && (
                  <Badge variant="outline" className="text-xs text-amber-600">Different from intended role</Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                {getRoleDescription(selectedRole)}
              </p>
            </div>

            <DeviceMultiSelect
              organizationId={contact.organization_id}
              selectedDeviceIds={selectedDeviceIds}
              onSelectionChange={setSelectedDeviceIds}
              disabled={isLoading}
            />

            <div className="bg-blue-50 p-3 rounded-lg">
              <p className="text-sm text-blue-800">
                {hasPendingInvitation 
                  ? `A new invitation email will be sent to ${contact.email} with instructions to create their account.`
                  : `An invitation email will be sent to ${contact.email} with instructions to create their account.`
                }
              </p>
            </div>
          </div>

          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setInviteOpen(false)} className="flex-1">
              Cancel
            </Button>
            <Button onClick={handleSendInvitation} disabled={isLoading} className="flex-1 bg-lime-button text-black hover:bg-lime-button/90">
              {isLoading ? (
                "Sending..."
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send Invitation
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
